import { HqoClientSdk, SdkEvents } from '@hqo/hqo-miniapp-client-sdk';
import { useMiniappSdkEventHandler } from 'app/shared/hooks/use-miniapp-sdk/use-miniapp-sdk.hooks';
import { useMiniappSdk } from 'app/shared/contexts/miniapp-sdk';
import { AccordionContent, AccordionItem, AccordionTrigger, Button } from '@hqo/ui-atoms';
import { FormattedMessage } from 'react-intl';
import React, { useCallback, useState } from 'react';

type ActionGroups = keyof Pick<HqoClientSdk, 'host' | 'identity' | 'payments' | 'storage' | 'header'>;

interface MiniappSdkMethodDemoProps<AG extends ActionGroups> {
  actionGroup: AG;
  action: keyof HqoClientSdk[AG];
  eventName: SdkEvents;
  children?: React.ReactNode;
  args?: unknown[];
}

export const MiniappSdkMethodDemo = <AG extends ActionGroups>({
  action,
  actionGroup,
  eventName,
  children,
  args = [],
}: MiniappSdkMethodDemoProps<AG>): JSX.Element => {
  const [eventData, setEventData] = useState<string | null>(null);
  const client = useMiniappSdk();
  const label = `${actionGroup}.${String(action)}`;

  useMiniappSdkEventHandler(
    eventName,
    (data: unknown) => {
      setEventData(JSON.stringify(data, null, 2));
    },
    [setEventData],
  );

  const handleClick = useCallback(() => {
    const emitAction = client[actionGroup][action];

    if (typeof emitAction === 'function') {
      emitAction(...args);
    }
  }, [action, actionGroup, args, client]);

  return (
    <AccordionItem value={label} className="rounded-lg border bg-background px-4 last:border-b">
      <AccordionTrigger className="font-mono text-sm">{label}</AccordionTrigger>
      <AccordionContent className="flex flex-col items-start gap-3 pb-4">
        {children}
        <Button onClick={handleClick} variant="default" size="sm" data-testid={`run-${label}`}>
          <FormattedMessage id="buttons.run" />
        </Button>
        {eventData && (
          <pre className="w-full overflow-x-auto rounded-md bg-muted p-3 text-xs">{eventData}</pre>
        )}
      </AccordionContent>
    </AccordionItem>
  );
};
